import React, { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiMinus, FiPlus, FiTrash2 } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import "../css/cartpage.scss";

const formatMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);

  const populatedItems = cartItems
    .map((item) => ({
      ...item,
      product: productData.find((product) => product.id === item.id),
    }))
    .filter((item) => item.product);

  const subtotal = populatedItems.reduce(
    (total, item) =>
      total + Number(item.product.price) * Number(item.quantity),
    0
  );

  if (populatedItems.length === 0) {
    return (
      <main className="cart-page cart-page--empty">
        <div className="cart-empty">
          <span className="cart-empty__eyebrow">Your collection awaits</span>
          <h1>Your shopping cart is empty.</h1>
          <p>
            Explore fresh notebooks, stickers, washi tape, and thoughtful desk
            accessories selected for people who love ink on paper.
          </p>
          <Link className="cart-button" to="/shop">
            Shop new releases
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <header className="cart-page__header">
        <span className="cart-page__eyebrow">ioomparade</span>
        <h1>Shopping Cart</h1>
      </header>

      <div className="cart-layout">
        <div className="cart-items" aria-label="Cart items">
          {populatedItems.map((item) => {
            const { product } = item;
            const itemKey = `${item.id}-${item.size}-${item.color}`;

            return (
              <article className="cart-item" key={itemKey}>
                <Link
                  className="cart-item__image-link"
                  to={`/shopDetails/${product.id}`}
                  aria-label={`View ${product.name}`}
                >
                  <img src={product.image} alt="" />
                </Link>

                <div className="cart-item__details">
                  <p className="cart-item__brand">{product.brand}</p>
                  <h2>
                    <Link to={`/shopDetails/${product.id}`}>
                      {product.name}
                    </Link>
                  </h2>
                  <p className="cart-item__options">
                    {item.color && <span>Style: {item.color}</span>}
                    {item.size && <span>Size: {item.size}</span>}
                  </p>
                  <button
                    className="cart-item__remove"
                    type="button"
                    onClick={() =>
                      removeFromCart(item.id, item.size, item.color)
                    }
                  >
                    <FiTrash2 aria-hidden="true" />
                    Remove
                  </button>
                </div>

                <div className="cart-item__purchase">
                  <div className="cart-quantity" aria-label="Quantity">
                    <button
                      type="button"
                      onClick={() =>
                        updateQuantity(
                          item.id,
                          item.size,
                          item.color,
                          Math.max(1, item.quantity - 1)
                        )
                      }
                      disabled={item.quantity <= 1}
                      aria-label={`Decrease quantity of ${product.name}`}
                    >
                      <FiMinus aria-hidden="true" />
                    </button>
                    <span aria-live="polite">{item.quantity}</span>
                    <button
                      type="button"
                      onClick={() =>
                        updateQuantity(
                          item.id,
                          item.size,
                          item.color,
                          item.quantity + 1
                        )
                      }
                      aria-label={`Increase quantity of ${product.name}`}
                    >
                      <FiPlus aria-hidden="true" />
                    </button>
                  </div>
                  <strong>
                    {formatMoney(Number(product.price) * item.quantity)}
                  </strong>
                </div>
              </article>
            );
          })}

          <Link className="cart-continue" to="/shop">
            Continue shopping
          </Link>
        </div>

        <aside className="cart-summary" aria-labelledby="summary-heading">
          <h2 id="summary-heading">Order summary</h2>
          <div className="cart-summary__row">
            <span>Subtotal</span>
            <strong>{formatMoney(subtotal)}</strong>
          </div>
          <div className="cart-summary__row cart-summary__row--muted">
            <span>Shipping</span>
            <span>{formatMoney(9.99)}</span>
          </div>
          <p className="cart-summary__note">
            Standard tracked shipping is a fixed $9.99. Any applicable taxes
            are shown by the payment service.
          </p>
          <button
            className="cart-button cart-button--full"
            type="button"
            onClick={() => navigate("/checkout")}
          >
            Check out
          </button>
          <p className="cart-summary__policy">
            Need help? Review our <Link to="/shipping">shipping</Link> and{" "}
            <Link to="/returns">returns</Link> information.
          </p>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
